import React, { FC } from 'react';
import {
	DashboardPerformerNames,
	DashboardPerformerSkeleton,
	useUrlParams,
} from '@aleph/shared-components';
import { SpotifyLevel, SpotifyLevelId } from 'wb-spotify/app/routes/superGrid/utils';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type NameCellPerformersParams = {
	selectedKPI: string | null
	performersType: 'bestPerformers' | 'worstPerformers'
	loading?: boolean
	performers?: any
};

const NameCellPerformers: FC<NameCellPerformersParams> = ({
	selectedKPI,
	performersType,
	loading,
	performers,
}) => {
	const navigate = useNavigate();
	const { setUrlParams, params: urlParams } = useUrlParams({ navigate, useParams });
	const { t } = useTranslation();

	const bestPerformerPlaceholders = [
		t('sp: best_performer', 'Top best performer'),
		t('sp: best_performer_2', '2nd best performer'),
		t('sp: best_performer_3', '3rd best performer'),
	];
	const worstPerformersPlaceholders = [
		t('sp: worst_performer_3', '3rd worst performer'),
		t('sp: worst_performer_2', '2nd worst performer'),
		t('sp: worst_performer', 'The worst performer'),
	];

	const omitViewToBreakdown = (id: string) => {
		setUrlParams({
			...urlParams,
			filters: {
				[SpotifyLevelId[SpotifyLevel.CAMPAIGN]]: [{ value: [id] }],
			},
			level: SpotifyLevel.GROUP,
		});
	};

	if (!selectedKPI) {
		return (
			<>
				{new Array(3).fill(null).map((_, idx) => (
					<div key={idx}
						className="flex h-[32px] justify-between border-dashed border-b-[1px]"
					>
						<div
							className={classNames('flex items-center pl-[12px] w-full text-[12px] hover:text-blue')}
						>
							<div className="whitespace-nowrap overflow-hidden overflow-ellipsis text-gray-600">
								{
									performersType === 'bestPerformers'
										? bestPerformerPlaceholders[idx]
										: worstPerformersPlaceholders[idx]
								}
							</div>
						</div>
						<div className={classNames('flex items-center text-[12px] font-semibold pr-[12px]', {
							'text-green': performersType === 'bestPerformers',
							'text-red':   performersType === 'worstPerformers',
						})}
						>
							{`${performersType === 'bestPerformers' ? '+' : '-'}%`}
						</div>
					</div>
				))}
			</>
		);
	}

	return (
		<div className="w-full h-[96px]">
			{loading && <DashboardPerformerSkeleton />}

			{
				performers
				&& <DashboardPerformerNames
					// @ts-ignore: TODO: fix type
					<DashboardStatsQuery['bestPerformers']['items'] | DashboardStatsQuery['worstPerformers']['items']>
					data={performers}
					selectedKpi={selectedKPI}
					onPerformerClick={performer => omitViewToBreakdown(performer.id)}
				/>
			}
		</div>
	);
};

export default NameCellPerformers;
